<template>
  <article class="outputs">
    <section class="field display">
      <label>{{ $tc('OUTPUT', 2) }}</label>

      <div class="ui blue label" v-for="output in outputs" :key="output.name">
        <a><i class="trash icon" @click="remove(output)"></i></a>
        {{ output.name }}
        <div class="detail">{{ getSubjectName(output.type) }}</div>
      </div>

      <span v-if="outputs && !outputs.length && !addVisible">
        {{ $t('NO_THING_YET', { thing: $tc('OUTPUT') }) }}
      </span>

      <button
        class="circular ui icon button"
        v-if="availableOutputs.length && !addVisible"
        @click.prevent="toggleAddVisible()"
        :disabled="isDisabled"
      >
        <i class="icon plus"></i>
      </button>
    </section>

    <section class="fields" v-if="addVisible">
      <typeahead
        :items="availableOutputs"
        :placeholder-text="$tc('OUTPUT', 1)"
        :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('OUTPUT', 2) })"
        :select-handler="outputSelected"
        :required="true"
        class="ten wide"
        :disabled="isDisabled"
      ></typeahead>

      <dropdown-select
        :items="possibleTypes"
        :placeholder-text="$tc('TYPE', 1)"
        :select-handler="typeSelected"
        :required="true"
        class="six wide"
        :disabled="isDisabled"
      ></dropdown-select>

      <footer>
        <button
          class="circular ui icon button"
          type="button"
          @click.prevent="done"
          :disabled="isDisabled"
        >
          <i class="icon checkmark"></i>
        </button>
      </footer>
    </section>
  </article>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';

.ui.label {
  margin: 3px;
}
</style>

<script>
// @flow
import Vue from 'vue'
import { Typeahead, DropdownSelect } from 'ui'
import { Subject, Toast } from 'services'
import { map, catchError } from 'rxjs/operators'

const possibleTypes = [{ name: 'String', id: 'string' }]

const defaultOutput = {
  name: '',
  type: possibleTypes[0].id
}

export default {
  name: 'flow-outputs',
  components: { Typeahead, DropdownSelect },

  props: {
    outputs: {
      type: Array,
      default: () => []
    },
    availableOutputs: {
      type: Array,
      default: () => []
    },
    addHandler: {
      type: Function,
      default: () => () => {}
    },
    removeHandler: {
      type: Function,
      default: () => () => {}
    },
    isDisabled: {
      type: Boolean,
      default: () => false
    }
  },

  data() {
    return {
      possibleTypes: [...possibleTypes],
      addVisible: false,
      output: { ...defaultOutput }
    }
  },

  methods: {
    readAllSubjects() {
      return Subject.readAllSubjects()
        .then(subjects => {
          this.possibleTypes = [...possibleTypes, ...subjects]
          this.output.type = this.possibleTypes.length ? this.possibleTypes[0].id : '' // Selects first subject in the dropdown
        })
        .catch(err => {
          if (err.response && err.response.status !== 404) {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('READ', { thing: this.$i18n.tc('TYPE', 2) })
              })
            )
          }

          console.error(err)
          throw err
        })
    },

    getSubjectName(subjectId: string) {
      const subject = this.possibleTypes.find(s => s.id === subjectId)
      return subject ? subject.name : ''
    },

    outputSelected(output: Object) {
      this.output.name = output.id
    },

    typeSelected(type: Object) {
      this.output.type = type.id
    },

    done() {
      // If there's an output selected, call the selection handler.
      if (this.output.name) {
        this.addHandler(this.output)
        this.output = { ...defaultOutput } // Reset ouput name / reset ouput type to first possible value
      }

      this.toggleAddVisible()
    },

    remove(output: Object) {
      if (!this.isDisabled) {
        this.removeHandler(output)
      }
    },

    toggleAddVisible() {
      this.addVisible = !this.addVisible
    }
  },

  mounted() {
    this.readAllSubjects()
  }
}
</script>
